import * as React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowLeft, Check } from "lucide-react";

interface ColorPreset {
  title: string;
  preset1: {
    light: string;
    subtle: string;
    muted: string;
    dark: string;
    primary: string;
    secondary: string;
  };
  preset2: {
    light: string;
    subtle: string;
    muted: string;
    dark: string;
    primary: string;
    secondary: string;
  };
}

interface PresetBlockProps {
  preset: ColorPreset;
  presetIndex: number;
  selectedPreset: { presetIndex: number; subPreset: 1 | 2 } | null;
  onPresetSelect: (presetIndex: number, subPreset: 1 | 2) => void;
}

const PresetBlock: React.FC<PresetBlockProps> = ({
  preset,
  presetIndex,
  selectedPreset,
  onPresetSelect,
}) => {
  const isPreset1Selected =
    selectedPreset?.presetIndex === presetIndex &&
    selectedPreset?.subPreset === 1;
  const isPreset2Selected =
    selectedPreset?.presetIndex === presetIndex &&
    selectedPreset?.subPreset === 2;

  return (
    <div className="w-full flex flex-col flex-shrink-0">
      <div className="text-base text-muted">{preset.title}</div>
      <div className="flex gap-2 mt-2">
        <div
          className={twMerge(
            "flex-1 rounded-lg border p-2 gap-2 flex flex-col cursor-pointer relative",
            isPreset1Selected ? "border-black border-2" : "border-border",
          )}
          onClick={() => void onPresetSelect(presetIndex, 1)}
        >
          {isPreset1Selected && (
            <div className="absolute -top-3 -right-3 z-10 w-6 h-6 bg-black rounded-full flex items-center justify-center">
              <Check size={16} className="text-white" />
            </div>
          )}
          <div
            className="rounded-lg p-2 gap-3 flex flex-row"
            style={{ backgroundColor: preset.preset1.light }}
          >
            <div className="w-1/2 flex items-start justify-center p-1 gap-1 flex-col">
              <div className="flex items-center gap-1">
                <div className="flex">
                  <span
                    className="text-[3px]"
                    style={{ color: preset.preset1.primary }}
                  >
                    ★
                  </span>
                  <span
                    className="text-[3px]"
                    style={{ color: preset.preset1.primary }}
                  >
                    ★
                  </span>
                  <span
                    className="text-[3px]"
                    style={{ color: preset.preset1.primary }}
                  >
                    ★
                  </span>
                  <span
                    className="text-[3px]"
                    style={{ color: preset.preset1.primary }}
                  >
                    ★
                  </span>
                  <span
                    className="text-[3px]"
                    style={{ color: preset.preset1.primary }}
                  >
                    ★
                  </span>
                </div>
                <span
                  className="text-[3px]"
                  style={{ color: preset.preset1.muted }}
                >
                  4.8 88,888+ Trusted Customer Reviews
                </span>
              </div>
              <span
                className="text-[9px] font-bold"
                style={{ color: preset.preset1.dark }}
              >
                Plant the Future, One Leaf at a Time.
              </span>
              <span
                className="text-[3px] font-normal"
                style={{ color: preset.preset1.muted }}
              >
                This is a short description that provides context for this
                section. Keep it brief and informative.
              </span>
              <Button
                variant="primary"
                size="sm"
                layoutClassName="w-[33px] h-[7px]"
                UNSAFE_className="text-[4px] rounded-[1px]"
                style={{
                  backgroundColor: preset.preset1.primary,
                  borderColor: preset.preset1.primary,
                }}
              >
                Button
              </Button>
            </div>
            <div
              className="h-[90px] w-1/2 rounded-sm"
              style={{ backgroundColor: preset.preset1.subtle }}
            />
          </div>
          <div className="flex h-4 relative">
            <div
              className="flex-1 h-4 rounded-full -mr-3 z-[5] relative"
              style={{ backgroundColor: preset.preset1.light }}
            />
            <div
              className="flex-1 h-4 rounded-full -mr-3 z-[4] relative"
              style={{ backgroundColor: preset.preset1.subtle }}
            />
            <div
              className="flex-1 h-4 rounded-full -mr-3 z-[3] relative"
              style={{ backgroundColor: preset.preset1.muted }}
            />
            <div
              className="flex-1 h-4 rounded-full -mr-3 z-[2] relative"
              style={{ backgroundColor: preset.preset1.dark }}
            />
            <div
              className="flex-1 h-4 rounded-full z-[1] relative"
              style={{ backgroundColor: preset.preset1.primary }}
            />
          </div>
        </div>
        <div
          className={twMerge(
            "flex-1 rounded-lg border p-2 gap-2 flex flex-col cursor-pointer relative",
            isPreset2Selected ? "border-black border-2" : "border-border",
          )}
          onClick={() => void onPresetSelect(presetIndex, 2)}
        >
          {isPreset2Selected && (
            <div className="absolute -top-3 -right-3 z-10 w-6 h-6 bg-black rounded-full flex items-center justify-center">
              <Check size={16} className="text-white" />
            </div>
          )}
          <div
            className="rounded-lg p-2 gap-3 flex flex-row"
            style={{ backgroundColor: preset.preset2.light }}
          >
            <div className="w-1/2 flex items-start justify-center p-1 gap-1 flex-col">
              <div className="flex items-center gap-1">
                <div className="flex">
                  <span
                    className="text-[3px]"
                    style={{ color: preset.preset2.primary }}
                  >
                    ★
                  </span>
                  <span
                    className="text-[3px]"
                    style={{ color: preset.preset2.primary }}
                  >
                    ★
                  </span>
                  <span
                    className="text-[3px]"
                    style={{ color: preset.preset2.primary }}
                  >
                    ★
                  </span>
                  <span
                    className="text-[3px]"
                    style={{ color: preset.preset2.primary }}
                  >
                    ★
                  </span>
                  <span
                    className="text-[3px]"
                    style={{ color: preset.preset2.primary }}
                  >
                    ★
                  </span>
                </div>
                <span
                  className="text-[3px]"
                  style={{ color: preset.preset2.muted }}
                >
                  4.8 88,888+ Trusted Customer Reviews
                </span>
              </div>
              <span
                className="text-[9px] font-bold"
                style={{ color: preset.preset2.dark }}
              >
                Plant the Future, One Leaf at a Time.
              </span>
              <span
                className="text-[3px] font-normal"
                style={{ color: preset.preset2.muted }}
              >
                This is a short description that provides context for this
                section. Keep it brief and informative.
              </span>
              <Button
                variant="primary"
                size="sm"
                layoutClassName="w-[33px] h-[7px]"
                UNSAFE_className="text-[4px] rounded-[1px]"
                style={{
                  backgroundColor: preset.preset2.primary,
                  borderColor: preset.preset2.primary,
                }}
              >
                Button
              </Button>
            </div>
            <div
              className="h-[90px] w-1/2 rounded-sm"
              style={{ backgroundColor: preset.preset2.subtle }}
            />
          </div>
          <div className="flex h-4 relative">
            <div
              className="flex-1 h-4 rounded-full -mr-3 z-[5] relative"
              style={{ backgroundColor: preset.preset2.light }}
            />
            <div
              className="flex-1 h-4 rounded-full -mr-3 z-[4] relative"
              style={{ backgroundColor: preset.preset2.subtle }}
            />
            <div
              className="flex-1 h-4 rounded-full -mr-3 z-[3] relative"
              style={{ backgroundColor: preset.preset2.muted }}
            />
            <div
              className="flex-1 h-4 rounded-full -mr-3 z-[2] relative"
              style={{ backgroundColor: preset.preset2.dark }}
            />
            <div
              className="flex-1 h-4 rounded-full z-[1] relative"
              style={{ backgroundColor: preset.preset2.primary }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const colorPresets: ColorPreset[] = [
  {
    title: "Playful",
    preset1: {
      light: "#F9F6F5",
      subtle: "#EFE1DD",
      muted: "#738487",
      dark: "#20393C",
      primary: "#DF529A",
      secondary: "#DF529A",
    },
    preset2: {
      light: "#FDFCFC",
      subtle: "#FDF0E6",
      muted: "#7A7B9C",
      dark: "#010109",
      primary: "#F08E80",
      secondary: "#F08E80",
    },
  },
  {
    title: "Bold",
    preset1: {
      light: "#FDFDFC",
      subtle: "#FBF7DA",
      muted: "#867A6E",
      dark: "#000000",
      primary: "#FD7E00",
      secondary: "#FD7E00",
    },
    preset2: {
      light: "#FFFFFF",
      subtle: "#F5FDDC",
      muted: "#606990",
      dark: "#0A174C",
      primary: "#E75B86",
      secondary: "#E75B86",
    },
  },
  {
    title: "Natural",
    preset1: {
      light: "#FCFCFC",
      subtle: "#F0F0E0",
      muted: "#615C4C",
      dark: "#23211A",
      primary: "#9F997F",
      secondary: "#9F997F",
    },
    preset2: {
      light: "#FCFBF8",
      subtle: "#FFF7E0",
      muted: "#5F735E",
      dark: "#284526",
      primary: "#EFC05D",
      secondary: "#EFC05D",
    },
  },
  {
    title: "Modern",
    preset1: {
      light: "#FEFCF2",
      subtle: "#D6D1C7",
      muted: "#756E61",
      dark: "#252422",
      primary: "#EF8257",
      secondary: "#EF8257",
    },
    preset2: {
      light: "#FCFAF3",
      subtle: "#F6F1DE",
      muted: "#3E3F5B",
      dark: "#15151E",
      primary: "#207F43",
      secondary: "#207F43",
    },
  },
  {
    title: "Professional",
    preset1: {
      light: "#FBFBFB",
      subtle: "#D0E2F3",
      muted: "#426685",
      dark: "#0C1013",
      primary: "#1469AB",
      secondary: "#1469AB",
    },
    preset2: {
      light: "#FAFAFA",
      subtle: "#E5E5E5",
      muted: "#8B7E68",
      dark: "#000000",
      primary: "#E6950E",
      secondary: "#E6950E",
    },
  },
  {
    title: "Elegant",
    preset1: {
      light: "#F9F8F6",
      subtle: "#DFD9E2",
      muted: "#64556D",
      dark: "#2F2833",
      primary: "#C9546B",
      secondary: "#C9546B",
    },
    preset2: {
      light: "#FFFBF2",
      subtle: "#FFF1D1",
      muted: "#6B5858",
      dark: "#210E00",
      primary: "#A60203",
      secondary: "#A60203",
    },
  },
  {
    title: "Futuristic",
    preset1: {
      light: "#FFFFFF",
      subtle: "#E0E5EB",
      muted: "#5B7B79",
      dark: "#0C0F13",
      primary: "#66FCF2",
      secondary: "#66FCF2",
    },
    preset2: {
      light: "#FFFFFF",
      subtle: "#F7D4CF",
      muted: "#554B58",
      dark: "#301542",
      primary: "#D174D2",
      secondary: "#D174D2",
    },
  },
];

interface DesignLibraryColorPresetsProps {
  styleLibrary: {
    colors: {
      light: string;
      subtle: string;
      muted: string;
      dark: string;
      primary: string;
      secondary: string;
      tertiary: string;
      accent: string;
    };
    typography?: any;
  } | null;
  onBack?: () => void;
}

export const DesignLibraryColorPresets: React.FC<
  DesignLibraryColorPresetsProps
> = ({ styleLibrary, onBack }) => {
  const projectId = useCurrentProjectId();
  const utils = trpcUtils;

  const { mutateAsync: updateStyleLibrary } =
    trpc.styles.createOrUpdateStyleLibrary.useMutation({
      onSuccess: () => {
        void utils.styles.getStyleLibrary.invalidate();
        void utils.webContainer.getFileTree.invalidate({ projectId });
      },
    });

  const [selectedPreset, setSelectedPreset] = React.useState<{
    presetIndex: number;
    subPreset: 1 | 2;
  } | null>(null);

  const handlePresetSelect = React.useCallback(
    async (presetIndex: number, subPreset: 1 | 2) => {
      if (!projectId) {
        return;
      }

      const preset = colorPresets[presetIndex];
      if (!preset) {
        return;
      }

      const selectedSubPreset =
        subPreset === 1 ? preset.preset1 : preset.preset2;

      setSelectedPreset({ presetIndex, subPreset });

      const updatedColors = {
        light: selectedSubPreset.light,
        subtle: selectedSubPreset.subtle,
        muted: selectedSubPreset.muted,
        dark: selectedSubPreset.dark,
        primary: selectedSubPreset.primary,
        secondary: selectedSubPreset.secondary,
        tertiary: "",
        accent: "",
      };

      await updateStyleLibrary({
        projectId,
        styleLibrary: {
          colors: updatedColors,
          typography: styleLibrary?.typography,
        },
      });
    },
    [projectId, styleLibrary, updateStyleLibrary],
  );

  return (
    <div className="flex flex-col items-start flex-[1_0_0] self-stretch min-h-0 rounded-lg border border-border bg-white shadow-sm">
      <div className="flex justify-between items-center self-stretch p-6">
        <div className="flex items-center gap-3">
          <IconButton
            variant="secondary"
            size="sm"
            layoutClassName="h-6 w-6"
            icon={<ArrowLeft size={16} className="text-default" />}
            onClick={() => {
              onBack?.();
            }}
          />
          <div className="text-lg font-semibold">Color Presets</div>
        </div>
      </div>
      <div className="flex flex-1 flex-col self-stretch overflow-y-auto no-scrollbar p-6 pt-0 gap-6">
        {colorPresets.map((preset, index) => (
          <PresetBlock
            key={index}
            preset={preset}
            presetIndex={index}
            selectedPreset={selectedPreset}
            onPresetSelect={(presetIndex, subPreset) =>
              void handlePresetSelect(presetIndex, subPreset)
            }
          />
        ))}
      </div>
    </div>
  );
};
